// Ported from ifc-engine-core/demo/viewer-core/src/mesh-source.ts (source commit
// 341c08d7b0d782e9250d273afdb9c5966b4c2c0b, same author, MIT OR Apache-2.0 -- see
// src/vendor/ifc-mesh-wasm-0.4.1/NOTICE.md for the currently vendored WASM's exact pin) via
// aec-project-controls's src/domain/ifcMeshSource.ts. This file holds only the pure wire-decode
// contract: no WASM binding and no Three.js dependency, matching this app's existing domain/integration split
// (compare src/domain/local-ifc-projection.ts, which is likewise WASM-free).

export const VERTEX_STRIDE_BYTES = 12;
const SUPPORTED_MESH_BUFFER_VERSION = 1;
const SUPPORTED_MANIFEST_VERSION = 1;

export type BboxMm = readonly [number, number, number, number, number, number];
export type IndexType = "u16" | "u32";

export interface VertexAttribute {
  readonly name: string;
  readonly type: string;
  readonly count: number;
  readonly offset: number;
}

export interface BufferRange {
  readonly byte_offset: number;
  readonly count: number;
}

export interface IndexRange extends BufferRange {
  readonly index_type: IndexType;
}

export interface ChunkHeader {
  readonly chunk_id: number;
  readonly storey_level: number;
  readonly quant_origin_mm: readonly [number, number, number];
  readonly quant_scale_mm: number;
  readonly bbox_mm: BboxMm;
  readonly vertices: BufferRange;
  readonly indices: IndexRange;
  readonly triangle_count: number;
}

export interface MeshBufferHeader {
  readonly mesh_buffer_version: number;
  readonly units: string;
  readonly vertex_layout: { readonly interleaved: boolean; readonly stride_bytes: number; readonly attributes: readonly VertexAttribute[] };
  readonly chunks: readonly ChunkHeader[];
  readonly guid_table: readonly string[];
}

export interface ManifestChunk {
  readonly chunk_id: number;
  readonly storey_level: number;
  readonly storey_name: string | null;
  readonly bbox_mm: BboxMm;
  readonly element_count: number;
}

export interface MeshManifest {
  readonly manifest_version: number;
  readonly model_bbox_mm: BboxMm;
  readonly chunks: readonly ManifestChunk[];
}

export class IfcEngineError extends Error {
  readonly op: string;
  readonly raw: string;
  constructor(op: string, message: string, raw: string) {
    super(`${op}: ${message}`);
    this.name = "IfcEngineError";
    this.op = op;
    this.raw = raw;
  }
}

export function toEngineError(op: string, thrown: unknown): IfcEngineError {
  if (thrown instanceof IfcEngineError) return thrown;
  const raw = typeof thrown === "string" ? thrown : thrown instanceof Error ? thrown.message : String(thrown);
  let message = raw;
  try {
    const parsed = JSON.parse(raw) as { error?: string };
    if (typeof parsed?.error === "string") message = parsed.error;
  } catch {
    // Not JSON (a panic string, a TypeError from the glue, ...): keep it verbatim.
  }
  return new IfcEngineError(op, message, raw);
}

function engineErrorEnvelope(value: unknown): string | null {
  if (typeof value === "object" && value !== null) {
    const inner = (value as Record<string, unknown>)["error"];
    if (typeof inner === "string") return inner;
  }
  return null;
}

const V1_VERTEX_ATTRIBUTES: readonly VertexAttribute[] = [
  { name: "position", type: "u16", count: 3, offset: 0 },
  { name: "normal_oct", type: "i8", count: 2, offset: 6 },
  { name: "instanceIndex", type: "u32", count: 1, offset: 8 }
];

function sameVertexAttributes(actual: readonly VertexAttribute[], expected: readonly VertexAttribute[]): boolean {
  if (actual.length !== expected.length) return false;
  return expected.every((exp) => actual.some((a) => a.name === exp.name && a.type === exp.type && a.count === exp.count && a.offset === exp.offset));
}

/**
 * Decodes and validates one `mesh_chunk` header (named test: "decodes a v1 mesh chunk header").
 * Refuses (throws) rather than guessing at an unsupported buffer version or vertex layout, matching
 * this app's no-silent-loss discipline.
 */
export function decodeChunkHeader(json: string): MeshBufferHeader {
  const header = JSON.parse(json) as MeshBufferHeader;
  const envelope = engineErrorEnvelope(header);
  if (envelope !== null) throw new IfcEngineError("mesh_chunk", envelope, json);
  if (header.mesh_buffer_version !== SUPPORTED_MESH_BUFFER_VERSION) {
    throw new Error(`unsupported mesh_buffer_version ${header.mesh_buffer_version} (this app decodes v${SUPPORTED_MESH_BUFFER_VERSION})`);
  }
  if (header.vertex_layout.stride_bytes !== VERTEX_STRIDE_BYTES || !header.vertex_layout.interleaved) {
    throw new Error(`unsupported vertex layout: interleaved=${String(header.vertex_layout.interleaved)} stride=${header.vertex_layout.stride_bytes}`);
  }
  if (!sameVertexAttributes(header.vertex_layout.attributes, V1_VERTEX_ATTRIBUTES)) {
    throw new Error(`unsupported vertex layout attributes: ${JSON.stringify(header.vertex_layout.attributes)}`);
  }
  return header;
}

/** Decodes and validates a `mesh_manifest` response (named test: "decodes a v1 mesh manifest"). */
export function decodeManifest(json: string): MeshManifest {
  const manifest = JSON.parse(json) as MeshManifest;
  const envelope = engineErrorEnvelope(manifest);
  if (envelope !== null) throw new IfcEngineError("mesh_manifest", envelope, json);
  if (manifest.manifest_version !== SUPPORTED_MANIFEST_VERSION) {
    throw new Error(`unsupported manifest_version ${manifest.manifest_version} (this app reads v${SUPPORTED_MANIFEST_VERSION})`);
  }
  return manifest;
}

export interface RawChunk {
  readonly header: string;
  readonly blob: Uint8Array;
}
